import { descargar, obtenerBlob, solicitar, subirArchivo } from '@nucleo/api/cliente'
import type { AlcanceEntrada, Catalogo, Fila, FilaEntrada, Matriz, PlanDeMedidas, RecursoDeMatriz, ResultadoDePropuesta, ResumenDeMatriz, Tarea, TareaEntrada, VistaPreviaOficial } from './tipos'

export function obtenerCatalogo(): Promise<Catalogo> {
  return solicitar<Catalogo>('GET', '/api/catalogo')
}

export function listarMatrices(): Promise<ResumenDeMatriz[]> {
  return solicitar<ResumenDeMatriz[]>('GET', '/api/matrices')
}

export function obtenerMatriz(id: string): Promise<Matriz> {
  return solicitar<Matriz>('GET', `/api/matrices/${id}`)
}

export function crearMatriz(alcance: AlcanceEntrada): Promise<Matriz> {
  return solicitar<Matriz>('POST', '/api/matrices', alcance)
}

export function actualizarAlcance(id: string, alcance: AlcanceEntrada): Promise<Matriz> {
  return solicitar<Matriz>('PUT', `/api/matrices/${id}/alcance`, alcance)
}

export function listarRecursos(id: string): Promise<RecursoDeMatriz[]> {
  return solicitar<RecursoDeMatriz[]>('GET', `/api/matrices/${id}/recursos`)
}

export function subirLogo(id: string, archivo: File): Promise<RecursoDeMatriz> {
  const datos = new FormData()
  datos.append('archivo', archivo)
  return subirArchivo<RecursoDeMatriz>(`/api/matrices/${id}/recursos/logo`, datos)
}

export function obtenerLogo(id: string): Promise<Blob> {
  return obtenerBlob(`/api/matrices/${id}/recursos/logo`)
}

export function subirPerfil(id: string, archivo: File, area: string, cargo: string): Promise<RecursoDeMatriz> {
  const datos = new FormData()
  datos.append('archivo', archivo)
  datos.append('area', area)
  datos.append('cargo', cargo)
  return subirArchivo<RecursoDeMatriz>(`/api/matrices/${id}/recursos/perfiles`, datos)
}

export function eliminarRecurso(id: string, recursoId: string): Promise<void> {
  return solicitar<void>('DELETE', `/api/matrices/${id}/recursos/${recursoId}`)
}

export function proponerLevantamiento(id: string, descripcion: string): Promise<ResultadoDePropuesta> {
  return solicitar<ResultadoDePropuesta>('POST', `/api/matrices/${id}/levantamiento/propuesta`, { descripcion })
}

export function agregarTarea(id: string, tarea: TareaEntrada): Promise<Tarea> {
  return solicitar<Tarea>('POST', `/api/matrices/${id}/tareas`, tarea)
}

export function editarTarea(id: string, tareaId: string, tarea: TareaEntrada): Promise<Tarea> {
  return solicitar<Tarea>('PUT', `/api/matrices/${id}/tareas/${tareaId}`, tarea)
}

export function decidirTarea(id: string, tareaId: string, decision: 'validar' | 'descartar', motivo?: string): Promise<Tarea> {
  return solicitar<Tarea>('POST', `/api/matrices/${id}/tareas/${tareaId}/decision`, { decision, motivo: motivo ?? null })
}

export function validarTareasEnLote(id: string): Promise<{ validadas: number }> {
  return solicitar<{ validadas: number }>('POST', `/api/matrices/${id}/tareas/decision-en-lote`)
}

export function proponerFilas(id: string): Promise<ResultadoDePropuesta> {
  return solicitar<ResultadoDePropuesta>('POST', `/api/matrices/${id}/filas/propuesta`)
}

export function agregarFila(id: string, fila: FilaEntrada): Promise<Fila> {
  return solicitar<Fila>('POST', `/api/matrices/${id}/filas`, fila)
}

export function editarFila(id: string, filaId: string, fila: FilaEntrada): Promise<Fila> {
  return solicitar<Fila>('PUT', `/api/matrices/${id}/filas/${filaId}`, fila)
}

export function decidirFila(id: string, filaId: string, decision: 'validar' | 'descartar', motivo?: string): Promise<Fila> {
  return solicitar<Fila>('POST', `/api/matrices/${id}/filas/${filaId}/decision`, { decision, motivo: motivo ?? null })
}

export function validarFilasEnLote(id: string): Promise<{ validadas: number }> {
  return solicitar<{ validadas: number }>('POST', `/api/matrices/${id}/filas/decision-en-lote`)
}

export interface EvaluacionPrevia {
  vep: number
  nivel: string
  nivel_etiqueta: string
  accion: string
  avisos: string[]
}

export function evaluacionPrevia(probabilidad: number, consecuencia: number): Promise<EvaluacionPrevia> {
  return solicitar<EvaluacionPrevia>('POST', '/api/evaluacion/previa', { probabilidad, consecuencia })
}

export function confirmarMatriz(id: string, confirmadoPor: string): Promise<{ numero: number; filas: number }> {
  return solicitar<{ numero: number; filas: number }>('POST', `/api/matrices/${id}/confirmacion`, {
    confirmado_por: confirmadoPor,
    declaro_revision_completa: true,
  })
}

export function descargarExportacion(id: string, nombre: string): Promise<void> {
  return descargar(`/api/matrices/${id}/exportacion.xlsx`, nombre)
}

export function obtenerVistaPreviaOficial(id: string): Promise<VistaPreviaOficial> {
  return solicitar<VistaPreviaOficial>('GET', `/api/matrices/${id}/vista-previa`)
}

export function obtenerPlanDeMedidas(id: string): Promise<PlanDeMedidas> {
  return solicitar<PlanDeMedidas>('GET', `/api/matrices/${id}/plan-medidas`)
}

export function descargarPlanDeMedidas(id: string, revision: number): Promise<void> {
  return descargar(`/api/matrices/${id}/plan-medidas.xlsx`, `Plan-medidas-rev${revision}.xlsx`)
}
